import { Handle, Node, NodeProps, Position } from '@xyflow/react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { usePortalTheme } from '../../../themes/PortalThemeProvider';
import { flowThemeTokens } from '../../../themes/flowTokens';
import { ENUM_NODE_HEIGHT, ENUM_NODE_WIDTH, EntityNodeData, EnumNodeData, NODE_HEIGHT, NODE_WIDTH } from '../DomainGraphUtils';

function EntityGraphNode({ data, selected }: NodeProps<Node<EntityNodeData>>) {
	const { themeName } = usePortalTheme();
	const nodeSurfaceColor = flowThemeTokens[themeName].nodeSurface;
	const referenceProperties = data.entity.properties.filter((property) => property.isReferenceProperty && property.referenceSchemaId);
	const enumProperties = data.entity.properties.filter((property) => property.isEnum && property.enumId);

	return (
		<Box
			sx={{
				width: NODE_WIDTH,
				minHeight: NODE_HEIGHT,
				border: `2px solid ${data.color}`,
				borderRadius: 1,
				bgcolor: nodeSurfaceColor,
				boxShadow: selected ? `0 0 0 4px ${data.color}66` : data.isAggregateRoot ? `0 0 0 3px ${data.color}33` : 2,
				overflow: 'hidden',
			}}
		>
			<Handle type="target" position={Position.Left} />
			<Stack spacing={1} sx={{ p: 1.5 }}>
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
					<Typography variant="subtitle2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
						{data.entity.name}
					</Typography>
					{data.isAggregateRoot && <Chip label="Root" size="small" sx={{ bgcolor: data.color, color: 'common.white', height: 22, borderRadius: 1 }} />}
				</Stack>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', minHeight: 32, overflowWrap: 'anywhere' }}>
					{data.entity.description || 'No description'}
				</Typography>
				<Stack spacing={0.5}>
					{referenceProperties.length === 0 && enumProperties.length === 0 ? (
						<Typography variant="caption" color="text.disabled">
							No references
						</Typography>
					) : (
						referenceProperties.slice(0, 3).map((property) => (
							<Typography key={property.name} variant="caption" sx={{ overflowWrap: 'anywhere' }}>
								{property.name} -&gt; {property.referenceSchemaShortName || 'Entity'}
							</Typography>
						))
					)}
					{enumProperties.slice(0, Math.max(0, 3 - referenceProperties.length)).map((property) => (
						<Typography key={property.name} variant="caption" sx={{ overflowWrap: 'anywhere' }}>
							{property.name} -&gt; Enum
						</Typography>
					))}
					{referenceProperties.length > 3 && (
						<Typography variant="caption" color="text.secondary">
							+{referenceProperties.length - 3} more
						</Typography>
					)}
				</Stack>
			</Stack>
			<Handle type="source" position={Position.Right} />
		</Box>
	);
}

function EnumGraphNode({ data, selected }: NodeProps<Node<EnumNodeData>>) {
	const { themeName } = usePortalTheme();
	const nodeSurfaceColor = flowThemeTokens[themeName].nodeSurface;

	return (
		<Box
			sx={{
				width: ENUM_NODE_WIDTH,
				minHeight: ENUM_NODE_HEIGHT,
				border: `2px dashed ${data.color}`,
				borderRadius: 1,
				bgcolor: nodeSurfaceColor,
				boxShadow: selected ? `0 0 0 4px ${data.color}66` : 2,
				overflow: 'hidden',
			}}
		>
			<Handle type="target" position={Position.Left} />
			<Stack spacing={1} sx={{ p: 1.5 }}>
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
					<Typography variant="subtitle2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
						{data.enumModel.name}
					</Typography>
					<Chip label="Enum" size="small" sx={{ bgcolor: data.color, color: 'common.black', height: 22, borderRadius: 1 }} />
				</Stack>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', minHeight: 28, overflowWrap: 'anywhere' }}>
					{data.enumModel.description || 'No description'}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{data.enumModel.items?.length ?? 0} values
				</Typography>
			</Stack>
		</Box>
	);
}

export const domainGraphNodeTypes = {
	entity: EntityGraphNode,
	enum: EnumGraphNode,
};
